import React from 'react';
import { Button, Select, Text, makeStyles } from '@fluentui/react-components';
import { ChevronLeft24Regular, ChevronRight24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-md)',
    marginTop: 'var(--space-md)',
    flexWrap: 'wrap',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    color: 'var(--color-text-secondary)',
    fontSize: '13px',
  },
  pages: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  pageBtn: {
    minWidth: '32px',
  },
});

interface PaginationProps {
  total: number;
  page: number;          // 1-based
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
}

function buildPageList(current: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | 'ellipsis')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) pages.push('ellipsis');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push('ellipsis');
  pages.push(totalPages);
  return pages;
}

export default function Pagination({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
}: PaginationProps) {
  const s = useStyles();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(total, safePage * pageSize);

  if (total === 0) {
    return null;
  }

  return (
    <div className={s.root}>
      <div className={s.left}>
        <Text size={200}>{from}–{to} of {total}</Text>
        <Select
          value={pageSize.toString()}
          onChange={(_, data) => onPageSizeChange(parseInt(data.value, 10))}
          style={{ minWidth: '70px' }}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size.toString()}>{size} / page</option>
          ))}
        </Select>
      </div>

      <div className={s.pages}>
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronLeft24Regular />}
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          aria-label="Previous page"
        />
        {buildPageList(safePage, totalPages).map((p, idx) =>
          p === 'ellipsis' ? (
            <Text key={`e${idx}`} size={200} style={{ padding: '0 4px', color: 'var(--color-text-tertiary)' }}>…</Text>
          ) : (
            <Button
              key={p}
              className={s.pageBtn}
              appearance={p === safePage ? 'primary' : 'subtle'}
              size="small"
              onClick={() => onPageChange(p)}
            >
              {p}
            </Button>
          )
        )}
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronRight24Regular />}
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
          aria-label="Next page"
        />
      </div>
    </div>
  );
}
